import { useCallback } from 'react'
import type { ContentItemViewNodesType } from '../../../types/contentItemViewNode.type'
import {
    checkParagraphLinkedToInactivePT,
    extractTextBeforeColon,
} from '../utils'
import { usePTProjectFilterCache } from './usePTProjectFilterCache'

/**
 * Hook that provides PT key paragraph validation utilities
 * Handles PT key paragraph validation and filtering logic
 */
export const usePTKeyParagraphValidator = () => {
    const { getPTElementNodesWithCache } = usePTProjectFilterCache()

    /**
     * Checks if a content item is directly a PT key paragraph linked to an INACTIVE PT
     * Used by Reviewers to determine if activation should be blocked
     * @param contentItem - The content item being checked
     * @returns true if this is a PT key paragraph linked to an inactive PT (not in project), false otherwise
     */
    const isDirectPTKeyParagraph = useCallback(
        (contentItem: Partial<ContentItemViewNodesType>): boolean => {
            // Must be a Paragraph node
            if (contentItem.textType !== 'Paragraph') {
                return false
            }

            const paragraphText = contentItem.elements?.[0]?.text
            if (!paragraphText || !paragraphText.includes(':')) {
                return false
            }

            // Extract PT name from paragraph text (text before colon)
            const ptName = extractTextBeforeColon(paragraphText)
            if (!ptName) {
                return false
            }

            // Get PT element nodes with cache
            const ptElementNodes = getPTElementNodesWithCache(contentItem)

            // Check if there's a matching PT element node (case-insensitive)
            const matchingPT = ptElementNodes.find((node) => {
                if (node.elementType !== 'PRODUCTTYPE') return false

                const nodePTName = extractTextBeforeColon(node.text || '')
                return nodePTName.toLowerCase() === ptName.toLowerCase()
            })

            // Return true only if PT exists AND is inactive (not in project)
            return !!matchingPT && !matchingPT.isInProject
        },
        [getPTElementNodesWithCache]
    )

    /**
     * Checks if a content item is a PT key paragraph linked to an INACTIVE PT (not in project)
     * Used by Reviewers to determine which PT paragraphs should stay hidden
     * @param contentItem - The content item to check
     * @returns true if it's a PT key paragraph with inactive PT, false otherwise
     */
    const isPTKeyParagraphWithInactivePT = useCallback(
        (contentItem: Partial<ContentItemViewNodesType>): boolean => {
            // Must be a paragraph with colon
            if (contentItem.textType !== 'Paragraph') {
                return false
            }

            const paragraphText = contentItem.elements?.[0]?.text
            if (!paragraphText?.includes(':')) {
                return false
            }

            // Extract PT name
            const ptName = extractTextBeforeColon(paragraphText)
            if (!ptName) {
                return false
            }

            // Get PT element nodes with cache
            const ptElementNodes = getPTElementNodesWithCache(contentItem)

            // Use existing utility to check for inactive PT
            const ptCheck = checkParagraphLinkedToInactivePT(
                paragraphText,
                ptElementNodes
            )

            // Return true if PT exists and is inactive (not in project)
            return ptCheck.hasInactivePT
        },
        [getPTElementNodesWithCache]
    )

    return {
        isDirectPTKeyParagraph,
        isPTKeyParagraphWithInactivePT,
    }
}
